import { motion } from 'framer-motion';
import { Users, Heart, Eye } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: 'easeOut' },
};

const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.1 },
  },
};

export default function About() {
  const { t } = useLanguage();

  const values = [
    {
      icon: Users,
      title: t('about.value1'),
      description: t('about.value1Desc'),
      color: 'emerald',
    },
    {
      icon: Heart,
      title: t('about.value2'),
      description: t('about.value2Desc'),
      color: 'red',
    },
    {
      icon: Eye,
      title: t('about.value3'),
      description: t('about.value3Desc'),
      color: 'blue',
    },
  ];

  const colorClasses = {
    emerald: {
      bg: 'bg-emerald-100',
      icon: 'text-emerald-600',
    },
    red: {
      bg: 'bg-red-100',
      icon: 'text-red-600',
    },
    blue: {
      bg: 'bg-blue-100',
      icon: 'text-blue-600',
    },
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 py-20 md:py-28" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-3xl"
          >
            <motion.p variants={fadeInUp} className="text-sm font-medium text-emerald-400 uppercase tracking-wider mb-3">
              {t('about.subtitle')}
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight">
              {t('about.title')}
            </motion.h1>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-28" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                {t('about.missionTitle')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                {t('about.missionText')}
              </p>
            </motion.div>
            <motion.div variants={fadeInUp} className="relative">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1683650666910-ecc317a12061?w=2400&h=1800&fit=crop&q=100"
                  alt="Happy Latino family"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">
              {t('about.valuesTitle')}
            </motion.h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                const colors = colorClasses[value.color];
                return (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="p-8 bg-white rounded-3xl border border-slate-100 text-center"
                    data-testid={`about-value-${index + 1}`}
                  >
                    <div className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center mb-6 mx-auto`}>
                      <Icon className={`w-7 h-7 ${colors.icon}`} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{value.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{value.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

    </Layout>
  );
}
